// Escritura de las marcas de «tramo revisado» (spec 2026-10-27 § 8): caché
// optimista, cola sin red y corrección con lo que devuelve el servidor.
import type { ApolloClient } from '@apollo/client';

import { isOfflineQueuedError } from '../../offline/isOfflineQueuedError';
import { SET_SEGMENT_REVIEWS } from '../mutations/segmentReviews';

/** Tope duro del backend (más de 200 ids = 400, y la cola descartaría el 400). */
const MAX_IDS_PER_REQUEST = 200;

export type SegmentReviewOutcome = 'saved' | 'queued' | 'failed';

type ReviewState = { reviewedAt: string; stale: boolean } | null;

export type ReviewResult = {
  segmentId: string;
  reviewed: boolean;
  changedAt?: string;
};

const cacheId = (segmentId: string) => `RouteSegment:${segmentId}`;

/** Escribe `myReview` del tramo y devuelve lo que había (para el rollback). */
function writeMyReview(
  cache: ApolloClient<unknown>['cache'],
  segmentId: string,
  next: ReviewState
): ReviewState | undefined {
  let previous: ReviewState | undefined;
  cache.modify({
    id: cacheId(segmentId),
    fields: {
      myReview(existing) {
        previous = existing ?? null;
        return next
          ? { __typename: 'RouteSegmentReviewState', ...next }
          : null;
      },
    },
  });
  return previous;
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

async function defaultNotifyError(): Promise<void> {
  try {
    const [{ default: i18n }, toasts] = await Promise.all([
      import('../../i18n'),
      import('../../utils/toasts'),
    ]);
    toasts.showErrorToast(i18n.t('pages:surveillance.segmentReview.saveError'));
  } catch {
    // El aviso es best-effort.
  }
}

export interface SetSegmentReviewsOptions {
  /** Hora del gesto (ISO). Sin ella, la de ahora. La genera quien guarda su gesto local. */
  changedAt?: string;
  /** Lo que respondió el servidor, por tramo (para adoptarlo si difiere del gesto). */
  onServerResults?: (results: ReviewResult[]) => void;
  /** Ids de los lotes que fallaron con red (para deshacer solo esos). */
  onFailedIds?: (ids: string[]) => void;
}

/**
 * Marca o desmarca tramos como revisados.
 *
 * 1. Escribe la caché ANTES de la red (la casilla responde en el toque).
 * 2. Manda la mutation en lotes de ≤ 200 ids, con la hora del gesto.
 * 3. Encolada sin red → `'queued'`: éxito diferido, la caché se queda optimista
 *    (y `usePendingSegmentReviews` la superpone tras recargar).
 * 4. El servidor manda: si devuelve otro estado, se corrige la caché.
 * 5. Error con red → rollback de los tramos de ese lote, aviso y `'failed'`.
 *
 * `notifyError` se inyecta para los tests; por defecto saca el toast.
 */
export async function setSegmentReviews(
  client: ApolloClient<unknown>,
  segmentIds: string[],
  reviewed: boolean,
  notifyError: () => void | Promise<void> = defaultNotifyError,
  options: SetSegmentReviewsOptions = {}
): Promise<SegmentReviewOutcome> {
  const ids = Array.from(new Set(segmentIds));
  if (ids.length === 0) return 'saved';

  const changedAt = options.changedAt ?? new Date().toISOString();
  const optimistic: ReviewState = reviewed
    ? { reviewedAt: changedAt, stale: false }
    : null;

  const previous = new Map<string, ReviewState | undefined>();
  for (const id of ids) previous.set(id, writeMyReview(client.cache, id, optimistic));

  let outcome: SegmentReviewOutcome = 'saved';

  for (const batch of chunk(ids, MAX_IDS_PER_REQUEST)) {
    try {
      const { data } = await client.mutate<{ setSegmentReviews: ReviewResult[] }>({
        mutation: SET_SEGMENT_REVIEWS,
        variables: { segmentIds: batch, reviewed, changedAt },
      });
      options.onServerResults?.(data?.setSegmentReviews ?? []);
      for (const result of data?.setSegmentReviews ?? []) {
        if (result.reviewed === reviewed) continue;
        writeMyReview(
          client.cache,
          result.segmentId,
          result.reviewed
            ? { reviewedAt: result.changedAt ?? changedAt, stale: false }
            : null
        );
      }
    } catch (err) {
      if (isOfflineQueuedError(err)) {
        if (outcome === 'saved') outcome = 'queued';
        continue;
      }
      for (const id of batch) {
        writeMyReview(client.cache, id, previous.get(id) ?? null);
      }
      options.onFailedIds?.(batch);
      outcome = 'failed';
    }
  }

  if (outcome === 'failed') {
    try {
      await notifyError();
    } catch {
      // El aviso es best-effort.
    }
  }
  return outcome;
}
